import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Building2, Plus, Bed, Users, Edit, Trash2, ChevronDown,
  ChevronUp, X, Check, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import PageLayout from '../components/PageLayout';

// ── Role color lookup ─────────────────────────────────────────
const getRoleColor = (roles = []) => {
  const r = roles.map(s => s.toLowerCase()).join(' ');
  if (/surgeon|surgery|physician|doctor|gyn|gi\b|podiatr|peds/.test(r)) return 'bg-orange-100 text-orange-800';
  if (/anesthesi|crna/.test(r)) return 'bg-yellow-100 text-yellow-800';
  if (/pacu|recovery/.test(r)) return 'bg-green-100 text-green-800';
  if (/nurse|tech|scrub|or\b/.test(r)) return 'bg-blue-100 text-blue-800';
  return 'bg-slate-100 text-slate-600';
};

// ── Participant badge ─────────────────────────────────────────
const ParticipantCard = ({ participant, onUnassign, showPreference = false }) => {
  const name = participant.profile?.first_name
    ? `${participant.profile.first_name} ${participant.profile.last_name || ''}`.trim()
    : participant.user?.name || 'Unknown';
  const roles = participant.profile?.work_roles || [];
  const roleColor = getRoleColor(roles);
  const pref = participant.hotel_pref;

  return (
    <div className="flex items-start justify-between gap-2 bg-white rounded-xl border border-slate-100 px-3 py-2.5">
      <div className="flex items-start gap-2 min-w-0">
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <User className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-slate-800">{name}</span>
            {roles.length > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${roleColor}`}>{roles[0]}</span>
            )}
          </div>
          {pref?.roommate_preference && (
            <p className="text-xs text-primary/70 mt-0.5">
              Prefers: "{pref.roommate_preference}"
            </p>
          )}
          {pref?.accommodation_notes && (
            <p className="text-xs text-amber-600 mt-0.5">
              Needs: "{pref.accommodation_notes}"
            </p>
          )}
          {!pref?.roommate_preference && !pref?.accommodation_notes && showPreference && (
            <p className="text-xs text-slate-300 mt-0.5 italic">No preference noted</p>
          )}
        </div>
      </div>
      {onUnassign && (
        <button
          onClick={onUnassign}
          className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0 mt-0.5"
          data-testid={`unassign-${participant.user_id}`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
const HotelManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tripId: tripIdParam } = useParams();
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(tripIdParam || searchParams.get('trip_id') || '');
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState({});            // hotelId → [room]
  const [assignments, setAssignments] = useState({}); // roomId → [participantObj]
  const [participants, setParticipants] = useState([]); // all for trip
  const [hotelPrefs, setHotelPrefs] = useState({});    // userId → pref obj
  const [loading, setLoading] = useState(false);
  const [expandedHotels, setExpandedHotels] = useState({});

  // Dialogs
  const [hotelDialog, setHotelDialog] = useState(null); // null | 'create' | hotel obj
  const [roomDialog, setRoomDialog] = useState(null);   // null | { hotelId, room? }
  const [hotelForm, setHotelForm] = useState({ name: '', is_primary: false, is_secondary: false, address: '', notes: '' });
  const [roomForm, setRoomForm] = useState({ room_name: '', room_type: 'Standard', bed_config: '2 Queen', max_occupancy: 4 });

  const fetchHotels = useCallback(async () => {
    const res = await api.get('/hotels');
    setHotels(res.data);
    const roomsData = {};
    for (const h of res.data) {
      const r = await api.get(`/hotels/${h.hotel_id}/rooms`);
      roomsData[h.hotel_id] = r.data;
      // Auto-expand hotels that have rooms
      if (r.data.length > 0) setExpandedHotels(prev => ({ ...prev, [h.hotel_id]: true }));
    }
    setRooms(roomsData);
  }, []);

  const fetchTripData = useCallback(async (tripId) => {
    if (!tripId) return;
    setLoading(true);
    try {
      const [participantsRes, assignmentsRes, prefsRes] = await Promise.all([
        api.get(`/trips/${tripId}/participants`),
        api.get(`/trips/${tripId}/room-assignments`),
        api.get(`/trips/${tripId}/hotel-preferences`),
      ]);

      setParticipants(participantsRes.data);

      // Build hotel pref map
      const prefMap = {};
      prefsRes.data.forEach(p => { prefMap[p.user_id] = p; });
      setHotelPrefs(prefMap);

      // Build assignments map: roomId → [enriched participant]
      const assignMap = {};
      for (const a of assignmentsRes.data) {
        const participant = participantsRes.data.find(p => p.user_id === a.user_id);
        if (!participant) continue;
        if (!assignMap[a.room_id]) assignMap[a.room_id] = [];
        assignMap[a.room_id].push({ ...participant, hotel_pref: prefMap[a.user_id] });
      }
      setAssignments(assignMap);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get('/trips').then(r => {
      setTrips(r.data);
      if (!selectedTrip && r.data.length > 0) {
        const first = r.data[0].trip_id;
        setSelectedTrip(first);
      }
    });
    fetchHotels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchHotels]);

  useEffect(() => {
    if (selectedTrip) {
      setSearchParams({ trip_id: selectedTrip }, { replace: true });
      fetchTripData(selectedTrip);
    }
  }, [selectedTrip, fetchTripData, setSearchParams]);

  const refresh = () => { fetchHotels(); if (selectedTrip) fetchTripData(selectedTrip); };

  // Assign / Unassign
  const handleAssign = async (roomId, userId) => {
    try {
      await api.post(`/rooms/${roomId}/assign`, { room_id: roomId, user_id: userId, trip_id: selectedTrip });
      toast.success('Assigned');
      fetchTripData(selectedTrip);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
  };

  const handleUnassign = async (roomId, userId) => {
    try {
      await api.delete(`/rooms/${roomId}/assign/${userId}`, { params: { trip_id: selectedTrip } });
      toast.success('Removed from room');
      fetchTripData(selectedTrip);
    } catch { toast.error('Failed to unassign'); }
  };

  // Hotel CRUD
  const openCreateHotel = () => {
    setHotelForm({ name: '', is_primary: false, is_secondary: false, address: '', notes: '' });
    setHotelDialog('create');
  };
  const openEditHotel = (h) => { setHotelForm(h); setHotelDialog(h); };
  const handleSaveHotel = async () => {
    try {
      if (hotelDialog === 'create') {
        await api.post('/hotels', hotelForm);
        toast.success('Hotel added');
      } else {
        await api.put(`/hotels/${hotelDialog.hotel_id}`, hotelForm);
        toast.success('Hotel updated');
      }
      setHotelDialog(null);
      fetchHotels();
    } catch { toast.error('Failed to save hotel'); }
  };
  const handleDeleteHotel = async (hotelId) => {
    if (!window.confirm('Delete this hotel and all its rooms?')) return;
    try {
      await api.delete(`/hotels/${hotelId}`);
      toast.success('Hotel deleted');
      fetchHotels();
    } catch { toast.error('Failed to delete'); }
  };

  // Room CRUD
  const openCreateRoom = (hotelId) => {
    setRoomForm({ room_name: '', room_type: 'Standard', bed_config: '2 Queen', max_occupancy: 4 });
    setRoomDialog({ hotelId });
  };
  const openEditRoom = (room) => { setRoomForm(room); setRoomDialog({ hotelId: room.hotel_id, room }); };
  const handleSaveRoom = async () => {
    try {
      const payload = { ...roomForm, hotel_id: roomDialog.hotelId };
      if (roomDialog.room) {
        await api.put(`/rooms/${roomDialog.room.room_id}`, payload);
        toast.success('Room updated');
      } else {
        await api.post(`/hotels/${roomDialog.hotelId}/rooms`, payload);
        toast.success('Room added');
      }
      setRoomDialog(null);
      fetchHotels();
    } catch { toast.error('Failed to save room'); }
  };
  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Delete this room? All assignments will be removed.')) return;
    try {
      await api.delete(`/rooms/${roomId}`);
      toast.success('Room deleted');
      refresh();
    } catch { toast.error('Failed to delete room'); }
  };

  // Unassigned participants
  const assignedUserIds = new Set(Object.values(assignments).flat().map(p => p.user_id));
  const unassigned = participants.filter(p => !assignedUserIds.has(p.user_id));

  // Room occupant count
  const occupantCount = (roomId) => (assignments[roomId] || []).length;

  const hotelActions = (
    <>
      <Select value={selectedTrip} onValueChange={setSelectedTrip}>
        <SelectTrigger className="w-44 sm:w-56 h-9 text-sm" data-testid="trip-select">
          <SelectValue placeholder="Select trip..." />
        </SelectTrigger>
        <SelectContent>
          {trips.map(t => <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" onClick={openCreateHotel} className="rounded-full bg-rojo hover:bg-rojo-dark h-9" data-testid="add-hotel-btn">
        <Plus className="w-4 h-4 mr-1.5" />Add Hotel
      </Button>
    </>
  );

  return (
    <PageLayout title="Hotels & Room Assignments" headerActions={hotelActions} maxWidth="max-w-6xl">
          {hotels.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Building2 className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No hotels configured yet</p>
              <Button onClick={openCreateHotel} className="rounded-full bg-rojo hover:bg-rojo-dark">
                <Plus className="w-4 h-4 mr-2" />Add First Hotel
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              {hotels.map(hotel => {
                const hotelRooms = rooms[hotel.hotel_id] || [];
                const isExpanded = expandedHotels[hotel.hotel_id] !== false;
                const totalAssigned = hotelRooms.reduce((sum, r) => sum + occupantCount(r.room_id), 0);

                return (
                  <div key={hotel.hotel_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                    data-testid={`hotel-${hotel.hotel_id}`}>
                    {/* Hotel header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <button onClick={() => setExpandedHotels(prev => ({ ...prev, [hotel.hotel_id]: !isExpanded }))}
                          className="text-slate-400 hover:text-primary">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="font-heading font-semibold text-slate-800">{hotel.name}</h2>
                            {hotel.is_primary && <Badge className="bg-primary/10 text-primary border-0 text-xs">Primary</Badge>}
                            {hotel.is_secondary && <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">Secondary</Badge>}
                          </div>
                          <p className="text-xs text-slate-400">
                            {hotelRooms.length} room{hotelRooms.length !== 1 ? 's' : ''}
                            {selectedTrip && ` · ${totalAssigned} assigned`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs rounded-full"
                          onClick={() => openCreateRoom(hotel.hotel_id)} data-testid={`add-room-${hotel.hotel_id}`}>
                          <Plus className="w-3 h-3 mr-1" />Add Room
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-primary"
                          onClick={() => openEditHotel(hotel)}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-300 hover:text-red-500"
                          onClick={() => handleDeleteHotel(hotel.hotel_id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Rooms */}
                    {isExpanded && (
                      <div className="p-4 space-y-3">
                        {hotelRooms.length === 0 ? (
                          <p className="text-sm text-slate-400 text-center py-4">
                            No rooms yet.{' '}
                            <button className="text-primary hover:underline"
                              onClick={() => openCreateRoom(hotel.hotel_id)}>Add first room</button>
                          </p>
                        ) : (
                          hotelRooms.map(room => {
                            const occupants = assignments[room.room_id] || [];
                            const isFull = occupants.length >= room.max_occupancy;

                            return (
                              <div key={room.room_id} className={`border rounded-xl p-4 transition-colors ${isFull ? 'border-green-200 bg-green-50/40' : 'border-slate-200 bg-slate-50/50'}`}
                                data-testid={`room-${room.room_id}`}>
                                {/* Room header */}
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Bed className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium text-slate-800 text-sm">{room.room_name}</span>
                                    <span className="text-xs text-slate-500">{room.bed_config}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isFull ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                      {occupants.length}/{room.max_occupancy}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-primary"
                                      onClick={() => openEditRoom({ ...room, hotel_id: hotel.hotel_id })}>
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-300 hover:text-red-500"
                                      onClick={() => handleDeleteRoom(room.room_id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>

                                {/* Occupants */}
                                <div className="space-y-2">
                                  {occupants.map(p => (
                                    <ParticipantCard
                                      key={p.user_id}
                                      participant={p}
                                      onUnassign={() => handleUnassign(room.room_id, p.user_id)}
                                    />
                                  ))}
                                </div>

                                {/* Assign dropdown */}
                                {!isFull && selectedTrip && unassigned.length > 0 && (
                                  <div className="mt-2">
                                    <Select onValueChange={(uid) => handleAssign(room.room_id, uid)}>
                                      <SelectTrigger className="h-8 text-xs border-dashed" data-testid={`assign-select-${room.room_id}`}>
                                        <SelectValue placeholder="+ Assign participant..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {unassigned.map(p => {
                                          const name = p.profile?.first_name
                                            ? `${p.profile.first_name} ${p.profile.last_name || ''}`.trim()
                                            : p.user?.name || 'Unknown';
                                          const pref = hotelPrefs[p.user_id];
                                          return (
                                            <SelectItem key={p.user_id} value={p.user_id}>
                                              <div className="flex flex-col">
                                                <span>{name}</span>
                                                {pref?.roommate_preference && (
                                                  <span className="text-xs text-primary/70">Prefers: "{pref.roommate_preference}"</span>
                                                )}
                                              </div>
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                                {isFull && (
                                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                                    <Check className="w-3 h-3" />Room full
                                  </p>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Unassigned participants panel */}
              {selectedTrip && (
                <div className="bg-white rounded-xl border border-slate-200" data-testid="unassigned-panel">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <h3 className="font-heading font-semibold text-slate-700 text-sm">
                        Unassigned Participants
                        <span className="ml-2 text-xs font-normal text-slate-400">({unassigned.length})</span>
                      </h3>
                    </div>
                    <span className="text-xs text-slate-400">{participants.length - unassigned.length} / {participants.length} assigned</span>
                  </div>
                  <div className="p-4">
                    {loading ? (
                      <div className="text-center py-6 text-slate-400 text-sm">Loading participants...</div>
                    ) : unassigned.length === 0 ? (
                      <div className="text-center py-6 text-green-600 text-sm flex items-center justify-center gap-2">
                        <Check className="w-4 h-4" />All participants have been assigned to rooms
                      </div>
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {unassigned.map(p => (
                          <ParticipantCard
                            key={p.user_id}
                            participant={{ ...p, hotel_pref: hotelPrefs[p.user_id] }}
                            showPreference
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

      {/* Hotel Dialog */}
      <Dialog open={hotelDialog !== null} onOpenChange={() => setHotelDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{hotelDialog === 'create' ? 'Add Hotel' : 'Edit Hotel'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">Hotel Name *</Label>
              <Input value={hotelForm.name} onChange={e => setHotelForm({ ...hotelForm, name: e.target.value })}
                placeholder="Quinta de las Flores" data-testid="hotel-name-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">Address</Label>
              <Input value={hotelForm.address || ''} onChange={e => setHotelForm({ ...hotelForm, address: e.target.value })}
                placeholder="Calle del Hotel, Antigua" />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={hotelForm.is_primary}
                  onCheckedChange={v => setHotelForm({ ...hotelForm, is_primary: v })} />
                <span className="text-sm">Primary hotel</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={hotelForm.is_secondary}
                  onCheckedChange={v => setHotelForm({ ...hotelForm, is_secondary: v })} />
                <span className="text-sm">Secondary hotel</span>
              </label>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">Notes</Label>
              <Input value={hotelForm.notes || ''} onChange={e => setHotelForm({ ...hotelForm, notes: e.target.value })}
                placeholder="Any notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHotelDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveHotel} disabled={!hotelForm.name} className="bg-rojo hover:bg-rojo-dark" data-testid="save-hotel-btn">
              {hotelDialog === 'create' ? 'Add Hotel' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Dialog */}
      <Dialog open={roomDialog !== null} onOpenChange={() => setRoomDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{roomDialog?.room ? 'Edit Room' : 'Add Room'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">Room Name / Number *</Label>
              <Input value={roomForm.room_name} onChange={e => setRoomForm({ ...roomForm, room_name: e.target.value })}
                placeholder="e.g., Room 101, Suite A" data-testid="room-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1 block">Bed Configuration</Label>
                <Input value={roomForm.bed_config} onChange={e => setRoomForm({ ...roomForm, bed_config: e.target.value })}
                  placeholder="2 Queen, 1 King..." />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1 block">Max Occupancy</Label>
                <Input type="number" min="1" max="8" value={roomForm.max_occupancy}
                  onChange={e => setRoomForm({ ...roomForm, max_occupancy: parseInt(e.target.value) || 1 })}
                  data-testid="max-occupancy-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoomDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveRoom} disabled={!roomForm.room_name} className="bg-rojo hover:bg-rojo-dark" data-testid="save-room-btn">
              {roomDialog?.room ? 'Save Changes' : 'Add Room'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default HotelManagement;
